import { useInView } from "react-intersection-observer"
import { ArrowUp, Home } from "lucide-react"
import { useEffect, useState } from "react"

import {
  Truck,
  Ship,
  Package,
  Shield,
  Globe,
  Thermometer,
  Clock,
  CheckCircle,
  MapPin,
  BarChart3,
  Award,
} from "lucide-react"
import "../assets/css/export.css"



export default function ExportSupplyChain() {
  const [showBackToTop, setShowBackToTop] = useState(false)
  
    useEffect(() => {
      const handleScroll = () => {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        setShowBackToTop(scrollTop > 300)
      }

      window.addEventListener("scroll", handleScroll)
      return () => window.removeEventListener("scroll", handleScroll)
    }, [])

  const goToHome = () => {
    window.location.href = "/"
  }

  const scrollToTop = () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      })
    }

  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  })

  const capabilities = [
    {
      icon: <Package className="capability-icon-svg" />,
      title: "Advanced Warehousing",
      description: "State-of-the-art storage facilities with temperature-controlled environments",
      features: [
        "Climate-controlled storage",
        "Inventory management systems",
        "Quality preservation",
        "Bulk storage capacity",
      ],
    },
    {
      icon: <Ship className="capability-icon-svg" />,
      title: "Global Shipping",
      description: "Comprehensive shipping solutions to over 50 countries worldwide",
      features: ["Sea freight services", "Air cargo options", "Express delivery", "Customs clearance"],
    },
    {
      icon: <Shield className="capability-icon-svg" />,
      title: "Quality Assurance",
      description: "Rigorous quality control throughout the supply chain process",
      features: ["HACCP certified", "ISO standards compliance", "Traceability systems", "Regular inspections"],
    },
    {
      icon: <Globe className="capability-icon-svg" />,
      title: "International Reach",
      description: "Serving markets across North America, Europe, Asia, and the Middle East",
      features: ["50+ countries served", "Regional distribution centers", "Local partnerships", "Market expertise"],
    },
  ]

  const exportMarkets = [
    { region: "North America", countries: "USA, Canada, Mexico", percentage: "35%" },
    { region: "Europe", countries: "UK, Germany, Netherlands, France", percentage: "30%" },
    { region: "Asia Pacific", countries: "Japan, Australia, Singapore, South Korea", percentage: "20%" },
    { region: "Middle East", countries: "UAE, Saudi Arabia, Qatar", percentage: "15%" },
  ]

  const certifications = [
    { name: "HACCP Certified", description: "Hazard Analysis Critical Control Points" },
    { name: "ISO 22000", description: "Food Safety Management Systems" },
    { name: "Global GAP", description: "Good Agricultural Practices" },
    { name: "Organic Certified", description: "USDA & EU Organic Standards" },
    { name: "Fair Trade", description: "Ethical Trading Practices" },
    { name: "BRC Certified", description: "British Retail Consortium Standards" },
  ]

  return (
    <section id="export-supply-chain" ref={ref} className="export-section">
      <div className="export-container">
        <div className="section-header">
          <h2 className={`section-title ${inView ? "visible" : ""}`}>Export & Supply Chain Excellence</h2>
          <div className={`section-divider ${inView ? "visible" : ""}`}></div>
          <p className={`section-subtitle ${inView ? "visible" : ""}`}>
            Delivering premium Sri Lankan agricultural products to international markets with world-class logistics and
            quality assurance
          </p>
        </div>

        {/* Main Capabilities Grid */}
        <div className="capabilities-grid">
          {capabilities.map((capability, index) => (
            <div
              key={index}
              className={`capability-card ${inView ? "visible" : ""}`}
              style={{
                transitionDelay: `${300 + index * 150}ms`,
              }}
            >
              <div className="capability-icon">{capability.icon}</div>
              <h3 className="capability-title">{capability.title}</h3>
              <p className="capability-description">{capability.description}</p>
              <ul className="capability-features">
                {capability.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="capability-feature">
                    <CheckCircle className="feature-check" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Supply Chain Process */}
        <div className={`supply-chain-process ${inView ? "visible" : ""}`}>
          <h3 className="process-title">Our Supply Chain Process</h3>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-icon">
                <Package />
              </div>
              <h4>Harvest & Processing</h4>
              <p>Fresh harvest with immediate processing and quality checks</p>
            </div>
            <div className="process-arrow">→</div>
            <div className="process-step">
              <div className="step-icon">
                <Thermometer />
              </div>
              <h4>Cold Storage</h4>
              <p>Temperature-controlled storage maintaining optimal freshness</p>
            </div>
            <div className="process-arrow">→</div>
            <div className="process-step">
              <div className="step-icon">
                <Truck />
              </div>
              <h4>Packaging & Loading</h4>
              <p>Professional packaging with proper labeling and documentation</p>
            </div>
            <div className="process-arrow">→</div>
            <div className="process-step">
              <div className="step-icon">
                <Ship />
              </div>
              <h4>International Shipping</h4>
              <p>Reliable shipping partners ensuring timely global delivery</p>
            </div>
          </div>
        </div>

        {/* Export Markets */}
        <div className={`export-markets ${inView ? "visible" : ""}`}>
          <div className="markets-content">
            <h3 className="markets-title">Global Market Presence</h3>
            <p className="markets-description">
              We proudly serve customers across four major regions, delivering authentic Sri Lankan agricultural
              products to international markets with consistent quality and reliability.
            </p>
            <div className="markets-grid">
              {exportMarkets.map((market, index) => (
                <div key={index} className="market-card">
                  <div className="market-header">
                    <MapPin className="market-icon" />
                    <h4 className="market-region">{market.region}</h4>
                    <span className="market-percentage">{market.percentage}</span>
                  </div>
                  <p className="market-countries">{market.countries}</p>
                  <div className="market-progress">
                    <div className="progress-bar" style={{ width: market.percentage }}></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="markets-image">
            <img src="/placeholder.svg?height=400&width=600" alt="Global Export Map" className="world-map" />
            <div className="map-overlay">
              <div className="stat-item">
                <BarChart3 className="stat-icon" />
                <div className="stat-content">
                  <span className="stat-number">50+</span>
                  <span className="stat-label">Countries Served</span>
                </div>
              </div>
              <div className="stat-item">
                <Clock className="stat-icon" />
                <div className="stat-content">
                  <span className="stat-number">7-14</span>
                  <span className="stat-label">Days Delivery</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className={`certifications-section ${inView ? "visible" : ""}`}>
          <h3 className="certifications-title">International Certifications & Standards</h3>
          <p className="certifications-description">
            Our commitment to quality is backed by internationally recognized certifications ensuring compliance with
            global food safety and quality standards.
          </p>
          <div className="certifications-grid">
            {certifications.map((cert, index) => (
              <div key={index} className="certification-card">
                <Award className="cert-icon" />
                <h4 className="cert-name">{cert.name}</h4>
                <p className="cert-description">{cert.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Warehouse Facilities */}
        <div className={`warehouse-facilities ${inView ? "visible" : ""}`}>
          <div className="facility-content">
            <h3 className="facility-title">State-of-the-Art Warehouse Facilities</h3>
            <div className="facility-features">
              <div className="facility-feature">
                <div className="feature-icon-large">
                  <Thermometer />
                </div>
                <div className="feature-details">
                  <h4>Climate Control</h4>
                  <p>
                    Advanced temperature and humidity control systems maintaining optimal storage conditions for
                    different product types.
                  </p>
                </div>
              </div>
              <div className="facility-feature">
                <div className="feature-icon-large">
                  <Shield />
                </div>
                <div className="feature-details">
                  <h4>Security Systems</h4>
                  <p>
                    24/7 monitoring with CCTV surveillance, access control, and fire safety systems ensuring product
                    security.
                  </p>
                </div>
              </div>
              <div className="facility-feature">
                <div className="feature-icon-large">
                  <Package />
                </div>
                <div className="feature-details">
                  <h4>Inventory Management</h4>
                  <p>Digital tracking systems with real-time inventory monitoring and automated stock management.</p>
                </div>
              </div>
            </div>
          </div>
          <div className="facility-stats">
            <div className="stat-card">
              <h4>Storage Capacity</h4>
              <span className="stat-value">10,000</span>
              <span className="stat-unit">MT</span>
            </div>
            <div className="stat-card">
              <h4>Cold Storage</h4>
              <span className="stat-value">2,500</span>
              <span className="stat-unit">MT</span>
            </div>
            <div className="stat-card">
              <h4>Processing Area</h4>
              <span className="stat-value">5,000</span>
              <span className="stat-unit">SqFt</span>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className={`export-cta ${inView ? "visible" : ""}`}>
          <div className="cta-content">
            <h3>Ready to Export Globally?</h3>
            <p>
              Partner with us for reliable, quality-assured supply chain solutions that deliver your products to
              international markets.
            </p>
            <div className="cta-buttons">
              <button className="cta-primary">Request Export Quote</button>
              <button className="cta-secondary">Download Capabilities Brochure</button>
            </div>
          </div>
        </div>
      </div>

      <div className="navigation-arrows">
        <button
          className={`nav-arrow back-to-top ${showBackToTop ? "visible" : ""}`}
          onClick={scrollToTop}
          data-tooltip="Back to Top"
          aria-label="Back to top"
        >
          <ArrowUp />
        </button>
        <button
          className="nav-arrow back-to-home"
          onClick={goToHome}
          data-tooltip="Back to Home"
          aria-label="Back to home"
        >
          <Home />
        </button>
      </div>

      {/* Background Elements */}
      <div className="bg-globe bg-globe-1"></div>
      <div className="bg-globe bg-globe-2"></div>
    </section>
  )
}
